<template>
  <div class="show sidebar-wrapper" @mouseover="showing = true" @mouseleave="showing = false">
    <vs-sidebar v-model="active" :hover-expand="autoExpand" :reduce="autoExpand" open :square="true" class="fixed-sidebar">
      <template #logo v-if="showing || !autoExpand">
        <img :src="require(`@/assets/platform/qrcode.png`)" @click="onTitleClick">
      </template>

      <div v-bind:class="{'spacing': !showing && autoExpand }"></div>

      <div class="sidebar-content">
        <div v-for="item in items">

        <!-- Single item -->
        <vs-sidebar-item 
          v-if="item.child == null"
          :id="item.id" 
          v-bind:key="item.id" 
          :to="isItemActive(item) ? item.id : null"
          :class="{ 'inactive-item': !isItemActive(item), 'disabled-item': !isItemActive(item) }"
          @click.native="handleItemClick(item, $event)"
        >
          <template #icon v-if="item.image == null">
            <i :class="item.icon"></i>
          </template>
          <img v-if="item.image" :src="require(`@/assets/platform/${item.image}`)" />
          <vs-tooltip v-if="!isItemActive(item)">
            <span class="inactive-icon">⚠️</span>
            {{item.title }}
            <template #tooltip>
              This item is currently inactive
            </template>
          </vs-tooltip>
          <template v-else>
            {{item.title }}
          </template>
        </vs-sidebar-item>
        <!-- Single item -->

        <!-- Group item -->
        <vs-sidebar-group :open="isParentActive(item)" v-else :class="['mt-4', { 'inactive-group': !isParentActive(item) }]">
          <template #header>
            <vs-sidebar-item class="icon-size" arrow :class="{ 'inactive-item': !isParentActive(item), 'disabled-item': !isParentActive(item) }" @click.native="handleHeaderClick(item, $event)">
              <template #icon>
                <i v-if="item.image == null" :class="item.icon"></i>
                <img v-else :src="require(`@/assets/platform/${item.image}`)" weight="20" height="20" class="mr-1" />
              </template>
              <vs-tooltip v-if="!isParentActive(item)">
                <span class="inactive-icon">⚠️</span>
                {{ item. title }}
                <template #tooltip>
                  This section is still under development
                </template>
              </vs-tooltip>
              <template v-else>
                {{ item. title }}
              </template>
            </vs-sidebar-item>
          </template>

          <template v-for="child in item.child">
            <vs-sidebar-item 
              :id="child.id" 
              :key="child.id"
              :to="isItemActive(child) ? child.id : null"
              :class="{ 'inactive-item': !isItemActive(child), 'disabled-item': !isItemActive(child) }"
              @click.native="handleItemClick(child, $event)"
            >
              <template #icon>
                <i v-if="child.image == null" :class="child.icon"></i>
                <img v-else :src="require(`@/assets/platform/${child.image}`)" weight="20" height="20" class="mr-1" />
              </template>
              <vs-tooltip v-if="!isItemActive(child)">
                <span class="inactive-icon">⚠️</span>
                {{ child.title }}
                <template #tooltip>
                  This item is still under development
                </template>
              </vs-tooltip>
              <template v-else>
                {{ child.title }}
              </template>
            </vs-sidebar-item>
          </template>
        </vs-sidebar-group>
        <!-- Group item -->

        </div>
      </div>

      <div justify="space-between" slot="footer" class="sidebar-footer">
        <vs-sidebar-item v-if="showDemo" id="demo" class="no-line" to="/demo">
          <template #icon>
            <i class="bx bx-play-circle"></i>
          </template>
          Demos
        </vs-sidebar-item>
        <vs-sidebar-item v-if="development" id="test-mode" class="no-line">
          <template #icon>
            <i class="bx bx-test-tube"></i>
          </template>
          <div class="test-mode-toggle">
            <span>Test Mode</span>
            <vs-switch v-model="testMode" @change="onTestModeChange" />
          </div>
        </vs-sidebar-item>
        <vs-sidebar-item  id="user" class="no-line">
          <template #icon>
            <i class="bx bx-user-circle"></i>
          </template>
          <vs-tooltip>
            Username: {{ email }}
            <template #tooltip>
              Editor ID: {{ editorId }}
            </template>
          </vs-tooltip>
        </vs-sidebar-item>
        <vs-sidebar-item v-if="showing || !autoExpand" id="logout" class="no-line" @click.native="onLogoutClick">
          <template #icon>
            <i class="bx bx-log-out"></i>
          </template>
          Logout
        </vs-sidebar-item>
      </div>
    </vs-sidebar>
    <ConfirmModal
      title="Logout"
      description="Are you sure you want to logout?"
      :show="showLogoutModal"
      @onClickYes="onLogoutConfirm"
      @onClickNo="onLogoutCancel"
    />
  </div>
</template>
<script>
import Vue from "vue"
import {mapGetters} from "vuex"
import Button from "@/base_lib/components/form/Button"
import ConfirmModal from "@/base_lib/components/container/ConfirmModal"
import ossMenuItems from "@/data/menu"
import {sidebarProps} from "@/base_lib/data/props/sidebarProps"
import routeMixin from "@/base_lib/data/mixins/routeMixin"
import router from "@/router"
import {filterMenuItemsByPermissions} from "@/utils/permissionHelper"

export default {
  name: "Sidebar",
  components: {Button, ConfirmModal},
  emits: ['onChange', 'onActive'],
  mixins: [routeMixin],
  props: sidebarProps,
  computed: {
    ...mapGetters({
      email: "auth/email",
      editorId: "auth/editorId",
      permissions: "auth/permissions",
    }),
    items() {
      let menuItems = this.$props.menuItems === null ? ossMenuItems : this.$props.menuItems
      
      // Always filter menu items based on permissions
      // Use empty array if permissions is not available yet
      const userPermissions = this.permissions || []
      menuItems = filterMenuItemsByPermissions(menuItems, userPermissions)
      
      // Sort children: active items first, inactive items last
      menuItems = menuItems.map(item => {
        if (item.child && Array.isArray(item.child)) {
          return {
            ...item,
            child: [...item.child].sort((a, b) => {
              const aActive = this.isItemActive(a)
              const bActive = this.isItemActive(b)
              if (aActive === bActive) return 0
              return aActive ? -1 : 1
            })
          }
        }
        return item
      })
      
      return menuItems
    },
    development() {
      return process.env.NODE_ENV === 'development'
    },
    showDemo() {
      return this.development && (this.showing || !this.autoExpand)
    },
  },
  data: () => ({
    showing: false,
    showLogoutModal: false,
    testMode: false,
  }),
  methods: {
    onItemClick(item) {
      console.log(item)
    },
    isItemActive(item) {
      // Default to true if isActive is not defined (backward compatibility)
      return item.isActive !== false
    },
    isParentActive(item) {
      // If parent has no children, check parent's isActive
      if (!item.child || !Array.isArray(item.child) || item.child.length === 0) {
        return this.isItemActive(item)
      }
      
      // Check if all children are inactive
      const allChildrenInactive = item.child.every(child => !this.isItemActive(child))
      
      // If all children are inactive, parent should be inactive
      if (allChildrenInactive) {
        return false
      }
      
      // Otherwise, check parent's own isActive flag
      return this.isItemActive(item)
    },
    handleItemClick(item, event) {
      if (!this.isItemActive(item)) {
        event.preventDefault()
        event.stopPropagation()
        return false
      }
    },
    handleHeaderClick(item, event) {
      // Prevent expanding if all children are inactive
      if (!this.isParentActive(item)) {
        event.preventDefault()
        event.stopPropagation()
        return false
      }
    },
    onLogoutClick(event) {
      event?.preventDefault()
      event?.stopPropagation()
      console.log('Logout clicked', this.showLogoutModal)
      this.showLogoutModal = true
      console.log('After setting modal', this.showLogoutModal)
    },
    onLogoutConfirm() {
      this.showLogoutModal = false
      // Ensure test mode cookie is completely removed
      Vue.$cookies.remove('testMode')
      Vue.$cookies.remove('testMode', '/')
      Vue.$cookies.remove('testMode', '', window.location.hostname)
      Promise.all([
        Vue.prototype.$stateManagement.dispatch('teams/logout'),
        Vue.prototype.$stateManagement.dispatch('auth/logout')
      ]).then(() => {
        router.push({ name: 'login' })
      }).catch(error => {
        router.push({ name: 'login' })
      })
    },
    onLogoutCancel() {
      this.showLogoutModal = false
    },
    onTestModeChange(value) {
      if (value) {
        Vue.$cookies.set('testMode', 'true', { expires: '1Y' })
      } else {
        // Remove cookie with all possible paths to ensure complete deletion
        Vue.$cookies.remove('testMode')
        Vue.$cookies.remove('testMode', '/')
        Vue.$cookies.remove('testMode', '', window.location.hostname)
        // Force reload to clear any cached state
        this.testMode = false
      }
    },
  },
  mounted() {
    // In production, ensure test mode is always disabled and cookie is removed
    if (process.env.NODE_ENV !== 'development') {
      Vue.$cookies.remove('testMode')
      Vue.$cookies.remove('testMode', '/')
      Vue.$cookies.remove('testMode', '', window.location.hostname)
      this.testMode = false
    } else {
      // Only read cookie in development
      const testModeCookie = Vue.$cookies.get('testMode')
      this.testMode = testModeCookie === 'true'
    }
  },
  watch: {
    showing: {
      immediate: true,
      handler(to) {
        this.$emit('onActive', to)
      }
    },
  },
}
</script>

<style>
.spacing {
  margin-top: 100px;
}
.no-line.vs-sidebar__item:after {
  background: transparent !important;
}
.vs-sidebar__item__icon {
  height: 32px !important;
}
.vs-sidebar__group__header .vs-sidebar__item__text {
  font-size: 0.9em !important;
  font-weight: 500;
}

.vs-sidebar__item__text {
  font-size: 0.8em !important;
  font-weight: 500;
}

.inactive-item {
  opacity: 0.5 !important;
}

.disabled-item {
  cursor: not-allowed !important;
}

.disabled-item.vs-sidebar__item {
  pointer-events: auto !important;
}

.disabled-item.vs-sidebar__item:hover {
  background-color: transparent !important;
}

.inactive-group {
  opacity: 0.5 !important;
}

/* Fix for closed inactive groups - header should align with regular items and be inactive */
.inactive-group.vs-sidebar__group:not(.vs-sidebar__group--open) {
  margin-left: 0 !important;
  padding-left: 0 !important;
}

.inactive-group.vs-sidebar__group:not(.vs-sidebar__group--open) .vs-sidebar__group__header {
  margin-left: 0 !important;
  padding-left: 0 !important;
}

.inactive-group.vs-sidebar__group:not(.vs-sidebar__group--open) .vs-sidebar__group__header .vs-sidebar__item {
  margin-left: 0 !important;
  padding-left: 0 !important;
  opacity: 0.5 !important;
  background-color: transparent !important;
}

/* Ensure inactive header items are properly styled even when group is open */
.inactive-group .vs-sidebar__group__header .inactive-item {
  opacity: 0.5 !important;
}

/* Prevent active styling on inactive closed groups */
.inactive-group.vs-sidebar__group:not(.vs-sidebar__group--open) .vs-sidebar__group__header .vs-sidebar__item:hover {
  background-color: transparent !important;
}

.inactive-group.vs-sidebar__group:not(.vs-sidebar__group--open) .vs-sidebar__group__header .vs-sidebar__item.vs-sidebar__item--active {
  background-color: transparent !important;
}

.inactive-icon {
  margin-right: 4px;
  font-size: 14px;
}

/* Fixed sidebar styles - no scroll, always visible */
.sidebar-wrapper {
  position: relative;
}

.fixed-sidebar {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  bottom: 0 !important;
  height: 100vh !important;
  overflow: hidden !important;
  z-index: 1000;
}

/* Ensure sidebar stays fixed after removing absolute prop */
.fixed-sidebar >>> .vs-sidebar {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  bottom: 0 !important;
  height: 100vh !important;
}

/* Ensure sidebar body uses flexbox and takes full height */
.fixed-sidebar >>> .vs-sidebar__body {
  display: flex !important;
  flex-direction: column !important;
  height: 100vh !important;
  overflow: hidden !important;
}

/* Sidebar content wrapper - scrollable menu items */
.sidebar-content {
  flex: 1 !important;
  overflow-y: auto !important;
  overflow-x: hidden !important;
  min-height: 0 !important;
}

/* Footer always at bottom - no scroll */
.sidebar-footer {
  flex-shrink: 0 !important;
  margin-top: auto !important;
}

/* Ensure footer slot is at bottom */
.fixed-sidebar >>> [slot="footer"] {
  flex-shrink: 0 !important;
  margin-top: auto !important;
}

.test-mode-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 10px;
}

.test-mode-toggle span {
  flex: 1;
}

</style>
